/**
 * useGeometryAutoPan Hook
 *
 * Wrapper hook that provides auto-panning functionality for all geometry types
 * with intelligent panel awareness (TaskForm vs GeometriesList).
 *
 * Features:
 * - Auto-pan support for markers, lines, and shapes
 * - Panel-aware boundary calculation:
 *   - When GeometriesList is open → uses GeometriesList width
 *   - When TaskForm is open → uses TaskForm width
 *   - When both closed → no panel offset
 * - Progressive speed based on distance to edge
 * - Velocity tracking to prevent pan during fast drags
 * - Mobile/desktop optimizations
 *
 * @module hooks/useGeometryAutoPan
 */

import { useCallback, useMemo } from 'react';
import { useMap } from '@vis.gl/react-maplibre';
import type { MapRef } from '@vis.gl/react-maplibre';
import { useMapAutoPan } from './useMapAutoPan';
import { useGeoMarkupStore } from '../store';
import { useDeviceLayout } from './useDeviceLayout';
import { MOBILE_DRAWING_BOTTOM_UI_HEIGHT } from '../constants/ui';

/**
 * Custom hook that wraps useMapAutoPan with panel-aware width calculation
 *
 * @param geometryId - The ID of the geometry being dragged
 * @returns Object containing auto-pan functions (smartPan, initializeVelocityTracking, stopAutoPan)
 *
 * @example
 * ```typescript
 * const { smartPan, initializeVelocityTracking, stopAutoPan } = useGeometryAutoPan(geometryId);
 *
 * const handleDragStart = (event) => {
 *   initializeVelocityTracking(event.lngLat);
 * };
 *
 * const handleDrag = (event) => {
 *   smartPan(event.lngLat);
 * };
 *
 * const handleDragEnd = () => {
 *   stopAutoPan();
 * };
 * ```
 */
export const useGeometryAutoPan = (geometryId: string) => {
  // Get map instance from MapLibre context
  const { current: map } = useMap();

  // Wrap map in ref-like structure for useMapAutoPan
  const mapRef = useMemo(() => {
    // Create a stable ref object that always points to the current map
    const ref = { current: map as MapRef | null };
    return ref;
  }, [map]);

  // Get drag state from Zustand
  const draggingGeometryId = useGeoMarkupStore((s) => s.draggingGeometryId);

  // Get panel visibility and width from Zustand
  const isGeometriesListOpen = useGeoMarkupStore(
    (s) => s.ui.geometriesList.isOpen
  );
  const isTaskFormOpen = useGeoMarkupStore((s) => s.tasks.formIsOpen);
  const panelWidth = useGeoMarkupStore((s) => s.ui.panelWidth);

  // Device detection for mobile-aware panel positioning
  const { isMobile } = useDeviceLayout();

  // Mobile panel state (minimized bar at bottom)
  const isMinimized = useGeoMarkupStore((s) => s.ui.slidePanel.isMinimized);

  /**
   * Dynamic panel width calculator
   * Returns the appropriate panel width based on which panel is currently visible
   *
   * Logic:
   * 1. Mobile: panel is at BOTTOM, not right → return 0 (don't reduce effective width)
   * 2. Desktop: If any panel (GeometriesList, TaskForm) is open → use shared panel width
   * 3. If no panels open → return 0 (no panel offset)
   */
  const getPanelWidth = useCallback(() => {
    // Mobile: panel is at BOTTOM, not right side - don't reduce effective width
    // This fixes the bug where negative distanceFromRight caused mega-acceleration
    if (isMobile) return 0;

    // Desktop: panel is on RIGHT side
    if (isGeometriesListOpen || isTaskFormOpen) {
      return panelWidth;
    }

    // No panels open
    return 0;
  }, [isMobile, isGeometriesListOpen, isTaskFormOpen, panelWidth]);

  /**
   * Dynamic panel height calculator for mobile bottom UI stack
   * Returns the height of form bar + toolbar during drawing mode
   */
  const getPanelHeight = useCallback(() => {
    // Desktop: panel is on RIGHT side, not bottom
    if (!isMobile) return 0;

    // Mobile: when minimized during drawing, UI stack at bottom:
    // form bar (84px) + spacing (16px) + toolbar (56px) = 156px
    if (isMinimized) {
      return MOBILE_DRAWING_BOTTOM_UI_HEIGHT;
    }

    return 0;
  }, [isMobile, isMinimized]);

  // Call useMapAutoPan with dynamic panel dimensions
  const { smartPan, initializeVelocityTracking, stopAutoPan } = useMapAutoPan({
    mapRef,
    isDragging: draggingGeometryId === geometryId,
    getPanelWidth,
    getPanelHeight,
  });

  return {
    smartPan,
    initializeVelocityTracking,
    stopAutoPan,
  };
};
